import { logger } from "../../services/logger.service";

/**
 * Did this request fail because the server does not have the route at all?
 *
 * A client can be newer than the server it talks to: during a deploy, and
 * while a screen is built ahead of its route. The piece that needs the route
 * is then simply not there yet, which calls for the same treatment as a
 * feature that is switched off (see featureDisabled.ts) and the opposite of a
 * failure:
 *
 *   route missing   render NOTHING for that piece, and say so once in the log.
 *   request failed  render a message saying so, with a way to retry.
 *
 * Matched on the whole answer that only the server's catch-all gives (see
 * notFoundHandler in server/middleware/errorHandler.ts): 404, the code
 * NOT_FOUND and the `path` that was asked for. A 404 on its own is not
 * enough. Real routes answer 404 too, some with no code at all ("that site is
 * not on this organisation's list"), and those are refusals a person has to be
 * told about.
 */
export function isRouteMissing(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { status?: unknown; data?: { code?: unknown; path?: unknown } | null };
  return err.status === 404 && err.data?.code === "NOT_FOUND" && typeof err.data.path === "string";
}

// Once per route per page load: a piece that is drawn in several places, or
// drawn again, must not fill the log with the same sentence.
const told = new Set<string>();

/**
 * Notes in the app's log that a piece was left out because its route is not
 * on this server. Names the route and nothing else: no instance, no person.
 */
export function noteRouteMissing(route: string): void {
  if (told.has(route)) return;
  told.add(route);
  logger.warn("Route not on this server; the part of the screen that needs it is left out", {
    component: "RouteMissing",
    route,
  });
}

/** For tests: forget what has been noted. */
export function forgetNotedRoutes(): void {
  told.clear();
}
